import { useRouteError } from "react-router-dom";
import { NavLink } from "react-router-dom";
import { MainNavigation } from "../components/MainNavigation";
import { Footer } from "../components/Footer";
import { Ribbon } from "../components/Ribbon";
import { ButtonLink } from "../components/ButtonLink";
import '../index.css'
import { Helmet } from "react-helmet";

const ErrorPage = () => {
    const error = useRouteError();
    const navHeight = '140px'
    const status = error?.status ?? 404;
    const detail = error?.data?.message || error?.data || error?.message;

    return (
        <>
            <MainNavigation></MainNavigation>
            <Helmet>
                <meta charSet="utf-8" />
                <title>Women Wavemakers - Page Not Found</title>
                <meta name="description" content="The page you're looking for doesn't exist on Women Wavemakers."></meta>
                <link rel="canonical" href="https:/womenwavemakers.com/error" />
            </Helmet>

            {/* HERO */}
            <section
                className="relative overflow-hidden bg-primary-dark px-6 pt-14 pb-24 md:px-12 md:pt-20 md:pb-36 flex items-center"
                style={{ minHeight: `calc(100vh - ${navHeight})` }}
            >
                <div className="pointer-events-none absolute -top-16 -left-16 w-72 h-72 rounded-full bg-primary-medium/30 blur-3xl"></div>
                <div className="pointer-events-none absolute -bottom-24 -right-16 w-96 h-96 rounded-full bg-accent-teal/30 blur-3xl"></div>
                <div className="pointer-events-none absolute top-1/3 right-1/4 w-44 h-44 rounded-full bg-secondary-regular/30 blur-3xl"></div>

                <div className="relative grid grid-cols-12 gap-8 items-center w-full">
                    <div className="col-span-12 lg:col-span-7 text-center lg:text-left">
                        <span className="font-work-sans font-medium uppercase tracking-wide text-sm text-accent-teal">
                            Error {status} &middot; Off The Map
                        </span>
                        <h1 className="font-sora font-black uppercase text-primary-light text-4xl sm:text-6xl md:text-7xl !leading-[1] mt-3">
                            You&rsquo;ve Dr<span className="text-secondary-regular">i</span>fted
                            <br />
                            Off C<span className="text-secondary-regular">o</span>urse.
                        </h1>
                        <p className="text-gray-200 font-source-sans font-light text-base md:text-lg mt-4 max-w-lg mx-auto lg:mx-0">
                            This page must&rsquo;ve caught a rogue wave and washed out to sea. Consider it a current, not a wrong turn.
                        </p>
                        <p className="font-work-sans font-medium uppercase tracking-wide text-base md:text-lg mt-6">
                            <span className="text-accent-teal">No apologies.</span>
                            <span className="text-primary-light/40 mx-2">|</span>
                            <span className="text-secondary-regular">Just a new course.</span>
                        </p>

                        <div className="flex flex-col sm:flex-row items-center gap-6 mt-8">
                            <div className="max-w-fit">
                                <ButtonLink toLink="/" linkText="Back To Shore"></ButtonLink>
                            </div>
                            <NavLink
                                to="/shop"
                                className="font-work-sans font-medium uppercase tracking-wide text-base text-accent-teal border-b border-accent-teal/40 hover:text-secondary-regular hover:border-secondary-regular/40 transition-colors pb-0.5"
                            >
                                or dive into the shop
                            </NavLink>
                        </div>

                        {detail && (
                            <p className="font-source-sans font-light text-primary-light/40 text-sm mt-8 italic">
                                {detail}
                            </p>
                        )}
                    </div>

                    <div className="col-span-12 lg:col-span-5 flex justify-center">
                        <div className="relative w-3/4 sm:w-3/5 lg:w-full max-w-xs">
                            <Ribbon tagName="M.I.A." customClasses="top-[0.75] -left-[10px]" />
                            <div className="p-1 rounded-xl bg-gradient-to-br from-primary-medium to-accent-teal shadow-2xl">
                                <div className="w-full aspect-[4/5] rounded-lg bg-gradient-to-b from-primary-deep to-primary-dark flex flex-col items-center justify-center overflow-hidden">
                                    <span className="font-sora font-black text-primary-light text-7xl sm:text-8xl leading-none">
                                        404
                                    </span>
                                    <svg
                                        width="180"
                                        height="28"
                                        viewBox="0 0 220 36"
                                        fill="none"
                                        className="text-accent-teal mt-4"
                                    >
                                        <path
                                            d="M2 20c14-16 28-16 42 0s28 16 42 0 28-16 42 0 28 16 42 0 28-16 42 0"
                                            stroke="currentColor"
                                            strokeWidth="3"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                    <span className="font-work-sans font-medium uppercase tracking-[0.3em] text-xs text-accent-teal/70 mt-4">
                                        Signal Lost
                                    </span>
                                </div>
                            </div>
                            <span className="absolute -bottom-3 -right-3 bg-white text-primary-dark font-work-sans font-medium text-base py-2 px-3 rounded-lg shadow-md">
                                $0.00 &middot; Not For Sale
                            </span>
                        </div>
                    </div>
                </div>
            </section>

            <Footer></Footer>
        </>
    )
}

export default ErrorPage
